import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { useIsMutating } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Merge } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";
import { useDispatch } from "react-redux";

import { Card } from "@/components/ui/card";
import { CAPABILITIES } from "@/constants/capabilities";
import { MUTATION_KEYS } from "@/constants/clientKeys";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";
import { useCapabilities, useIsDoctorView } from "@/hooks/queries/use-me";
import { stripBundlePrefix } from "@/utils/bundle-name";
import { getFileUrl } from "@/utils/file";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";
import type { GetAllFilesApiV1FilesGetData } from "@api/generated/types.gen";

import { FeatureAccessNotice } from "@components/FeatureAccessNotice";
import { DeleteMultipleFilesConfirmation } from "@components/FilesListDataTable";
import { ResizablePanels } from "@components/ResizablePanels";
import { Button } from "@components/ui/button";

import { useMergeFiles, useUnmergeFile } from "@hooks/mutations/use-file-merge";
import { useFile, useFiles } from "@hooks/queries/use-files";
import { useObjectUrl } from "@hooks/useObjectUrl";

import { useCaseDetail } from "@pages/ReportStudio/CaseDetail/context";

import { setMessage } from "@redux/notification";

import { DocumentChronologicalView } from "./components/DocumentChronologicalView";
import { DocumentPreview } from "./components/DocumentPreview";
import { DocumentTimelineView } from "./components/DocumentTimelineView";
import { DocumentTypeView } from "./components/DocumentTypeView";
import { FileFilter } from "./components/FileFilter";
import { MergeDocumentsModal } from "./components/MergeDocumentsModal";
import { SearchTypeSelector } from "./components/SearchTypeSelector";
import { SearchType } from "./components/SearchTypeSelector";
import { SemanticSearchInput } from "./components/SemanticSearchInput";
import { SummaryPanel } from "./components/SummaryPanel";
import { UploadBundleFile } from "./components/UploadBundleFile";
import { UploadDocumentsModal } from "./components/UploadDocumentsModal";

const DocumentSorterPage = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };
  const dispatch = useDispatch();
  const { setRightContent } = useCaseDetail();

  const isDeleting =
    useIsMutating({ mutationKey: MUTATION_KEYS.BULK_REMOVE_FILES }) > 0;

  const [searchType, setSearchType] = useState<SearchType>("document_type");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [filters, setFilters] = useState<GetAllFilesApiV1FilesGetData["query"]>({
    case_number: caseNumber,
    status: undefined,
    classification: undefined,
    created_at: undefined,
    issue_date: undefined,
    sort_by: "recently_added",
  });

  const [selectedSnapshot, setSelectedSnapshot] =
    useState<DocSorterFileSchema | null>(null);

  // MDRS-503: the preview URL is now an object URL for proxied bytes, so it
  // has to be revoked when the selected document changes — otherwise a session
  // over a case bundle pins one full PDF per file opened.
  const [fileUrl, setFileUrl] = useObjectUrl();
  const [fileType, setFileType] = useState<string | null>(null);
  const { accounts } = useMsal();
  const [showSummaryPanel, setShowSummaryPanel] = useState(false);
  const [, setIsUploadBundleFileOpen] = useState(false);
  const [, setIsUploadDocumentsModalOpen] = useState(false);
  // MDRS-420: doctors (expert-view users) manage their files individually —
  // merge, delete, and bulk-select aren't a doctor action, so all three must
  // disappear entirely rather than just being disabled.
  const isDoctorView = useIsDoctorView();
  const { can, isLoading: isCapabilitiesLoading } = useCapabilities();
  const canManageFiles = !isDoctorView;
  const isMergeMode = canManageFiles;
  const [mergeSelectedFiles, setMergeSelectedFiles] = useState<string[]>([]);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  // MDRS-408: "View Summary" swaps the page's layout tree (ResizablePanels ->
  // grid), remounting the active list view and resetting its scroll
  // position. Track the last-reported offset here so it survives the
  // remount and can be handed back to the freshly-mounted view.
  const scrollRestoreRef = useRef(0);
  const handleScrollTopChange = useCallback((top: number) => {
    scrollRestoreRef.current = top;
  }, []);

  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const {
    data: files = [],
    isLoading,
    refetch: refetchFiles,
  } = useFiles({
    case_number: caseNumber || "",
    sort_by:
      filters?.sort_by ??
      (searchType === "chronological" ? "recently_added" : undefined),
    classification: filters?.classification,
    status: filters?.status,
    created_at: filters?.created_at,
    created_at_end: filters?.created_at_end,
  });

  const listedSelection = selectedSnapshot
    ? files.find((file) => file.id === selectedSnapshot.id)
    : undefined;
  // Keep the snapshot current so a filter change never falls back to click-time data.
  if (listedSelection && listedSelection !== selectedSnapshot) {
    setSelectedSnapshot(listedSelection);
  }
  // A filter can hide the selected file; fetch it by id so saves still show.
  const { data: hiddenSelection } = useFile({
    fileId: selectedSnapshot?.id ?? "",
    enabled: !!selectedSnapshot && !listedSelection,
  });
  const selectedDocument =
    listedSelection ??
    (hiddenSelection?.id === selectedSnapshot?.id ? hiddenSelection : undefined) ??
    selectedSnapshot;

  // Merge and unmerge hooks
  const { mutate: mergeFiles, isPending: isMerging } = useMergeFiles();
  const { mutate: unmergeFile } = useUnmergeFile();

  const handleOpenMergeModal = useCallback(() => {
    if (mergeSelectedFiles.length >= 2) {
      setIsMergeModalOpen(true);
    }
  }, [mergeSelectedFiles.length]);

  // Set page header actions. MDRS-443: gated on the same capability as the
  // body below — otherwise a denied user still sees (and can click) the
  // Upload buttons in the header even though the content area refuses them.
  useEffect(() => {
    if (isCapabilitiesLoading || !can(CAPABILITIES.ASSESSMENT_PREP)) {
      setRightContent(null);
      return;
    }

    setRightContent(
      <div className="flex gap-2">
        <UploadBundleFile
          caseNumber={caseNumber}
          onToastOpen={() => setIsUploadBundleFileOpen(true)}
          onToastClose={() => setIsUploadBundleFileOpen(false)}
        />
        <UploadDocumentsModal
          caseNumber={caseNumber}
          onModalOpen={() => setIsUploadDocumentsModalOpen(true)}
          onModalClose={() => setIsUploadDocumentsModalOpen(false)}
        />
      </div>
    );

    return () => {
      setRightContent(null);
    };
  }, [caseNumber, setRightContent, can, isCapabilitiesLoading]);

  const organizedFiles = useMemo(() => {
    if (searchType === "chronological") {
      return [...files].sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
    } else if (searchType === "document_type") {
      const groupedByType: Record<string, DocSorterFileSchema[]> = {};

      files.forEach((file) => {
        const type = file.classification || "Uncategorized";
        if (!groupedByType[type]) {
          groupedByType[type] = [];
        }
        groupedByType[type].push(file);
      });

      // Sort groups and files within each group by document date
      const sortedGroupKeys = Object.keys(groupedByType).sort();
      return sortedGroupKeys.flatMap((key) =>
        groupedByType[key].sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA; // Most recent first
        })
      );
    } else if (searchType === "timeline") {
      // Timeline view doesn't need special organization, it's handled in the DocumentTimelineView component
      return files;
    } else {
      // Default view (FilesListWithDragDrop) - return files as is
      return files;
    }
  }, [files, searchType]);

  // Memoize the file object to prevent unnecessary re-renders
  const documentPreviewFile = useMemo(() => {
    if (!selectedDocument) return null;
    return {
      id: selectedDocument.id || "",
      name: selectedDocument.name || "",
      classification: selectedDocument.classification || "",
      date: selectedDocument.date,
      case: selectedDocument.case || { id: "", case_number: "" },
      full_summary: selectedDocument.full_summary || "",
    };
  }, [selectedDocument]);

  const previewFileId = selectedDocument?.id;
  const previewFileName = selectedDocument?.name;
  // Bumped by the preview's Retry to re-download the same file.
  const [previewAttempt, setPreviewAttempt] = useState(0);

  // Fetch fileUrl and fileType when the previewed file (or a Retry) changes
  useEffect(() => {
    let cancelled = false;
    async function fetchFileUrl() {
      if (!previewFileId || !previewFileName) {
        setFileUrl(null);
        setFileType(null);
        return;
      }
      try {
        const result = await getFileUrl({
          id: previewFileId,
          name: previewFileName,
        });
        if (cancelled) {
          // Selection moved on mid-fetch; release bytes nothing will render.
          if (result.url?.startsWith("blob:")) URL.revokeObjectURL(result.url);
          return;
        }
        setFileUrl(result.url || null);
        setFileType(result.type || null);
      } catch {
        setFileUrl(null);
        setFileType(null);
      }
    }
    fetchFileUrl();
    return () => {
      cancelled = true;
    };
  }, [previewFileId, previewFileName, previewAttempt, accounts]);

  if (!caseNumber) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-destructive">Invalid case</p>
      </div>
    );
  }

  // MDRS-443: the case-detail tab bar only hides this tab's *trigger* when the
  // capability is off — it never blocks the routed content itself, so a tab
  // already open when the capability is revoked, or a direct URL nav straight
  // to this route, both reached the page regardless. Re-checked here.
  //
  // `/users/me` starts with an empty capability set while it's still
  // loading, so `can()` would read false transiently — checking
  // isCapabilitiesLoading first (same pattern as AdminRoute) avoids
  // flashing the access notice at a fully-authorized user on cold load.
  if (isCapabilitiesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ThreeDots />
      </div>
    );
  }

  if (!can(CAPABILITIES.ASSESSMENT_PREP)) {
    return (
      <FeatureAccessNotice
        title="You don't have access to Assessment Prep."
        body="Ask an admin to enable this feature for your account."
      />
    );
  }

  const handleSelectFile = (fileId: string, selected: boolean) => {
    if (selected) {
      setSelectedFiles((prev) => [...prev, fileId]);
    } else {
      setSelectedFiles((prev) => prev.filter((id) => id !== fileId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedFiles(organizedFiles.map((file) => file.id));
    } else {
      setSelectedFiles([]);
    }
  };

  const handleViewFile = (file: DocSorterFileSchema | null) => {
    setSelectedSnapshot(file);
    setShowSummaryPanel(false);
  };

  const handleViewSummary = (file: DocSorterFileSchema) => {
    setSelectedSnapshot(file);
    setShowSummaryPanel(true);
  };

  const handleSearchTypeChange = (type: SearchType) => {
    setSearchType(type);
    refetchFiles();
    // Reset selected document when changing search types to ensure only Panel 1 is visible
    setSelectedSnapshot(null);
    setShowSummaryPanel(false);
    // MDRS-408: scrollRestoreRef holds the outgoing view's offset — switching
    // views mounts a different component, so carrying it over would apply
    // one view's scroll position to an unrelated one.
    scrollRestoreRef.current = 0;
    if (type === "timeline") {
      setSelectedFiles([]);
      setMergeSelectedFiles([]);
    }
  };

  const handleFiltersChange = (
    newFilters: GetAllFilesApiV1FilesGetData["query"]
  ) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const handleSelectFileForMerge = (fileId: string, selected: boolean) => {
    if (selected) {
      setMergeSelectedFiles((prev) => [...prev, fileId]);
      setSelectedFiles((prev) => [...prev, fileId]);
    } else {
      setMergeSelectedFiles((prev) => prev.filter((id) => id !== fileId));
      setSelectedFiles((prev) => prev.filter((id) => id !== fileId));
    }
  };

  const handleSelectAllForMerge = (selected: boolean) => {
    if (selected) {
      const allIds = organizedFiles.map((file) => file.id);
      setMergeSelectedFiles(allIds);
      setSelectedFiles(allIds);
    } else {
      setMergeSelectedFiles([]);
      setSelectedFiles([]);
    }
  };

  const handleMerge = (category: string, orderedFileIds: string[]) => {
    // Show loading toast
    dispatch(
      setMessage({
        content: `Merging ${orderedFileIds.length} files`,
        type: "info",
        isLoading: true,
      })
    );

    mergeFiles(
      {
        file_ids: orderedFileIds,
        target_classification: category,
        file_order: orderedFileIds,
      },
      {
        onSuccess: (data) => {
          // Reset state
          setMergeSelectedFiles([]);
          setIsMergeModalOpen(false);

          // Show success toast with undo button
          dispatch(
            setMessage({
              content: `Successfully merged ${orderedFileIds.length} files into "${stripBundlePrefix(data.merged_file.name)}".`,
              type: "success",
              action: {
                label: "Undo",
                onClick: () =>
                  handleUndoMerge(data.merged_file.id, data.merged_file.name),
              },
            })
          );
        },
        onError: (error) => {
          dispatch(
            setMessage({
              content: `Failed to merge files: ${error.message}`,
              type: "error",
            })
          );
        },
      }
    );
  };

  const handleUndoMerge = (mergedFileId: string, mergedFileName?: string) => {
    const fileName = mergedFileName || "merged file";

    // Show loading toast
    dispatch(
      setMessage({
        content: `Unmerging "${fileName}"`,
        type: "info",
        isLoading: true,
      })
    );

    unmergeFile(mergedFileId, {
      onSuccess: () => {
        dispatch(
          setMessage({
            content: `Successfully unmerged "${fileName}". Original files restored.`,
            type: "success",
          })
        );
      },
      onError: (error) => {
        dispatch(
          setMessage({
            content: `Failed to undo merge: ${error.message}`,
            type: "error",
          })
        );
      },
    });
  };

  const renderDocumentView = () => {
    const commonProps = {
      files,
      onViewFile: handleViewFile,
      onViewSummary: handleViewSummary,
      isLoading,
      selectedDocumentId: selectedDocument?.id,
      showSummaryPanel,
      initialScrollTopRef: scrollRestoreRef,
      onScrollTopChange: handleScrollTopChange,
      canManageFiles,
    };

    const selectionProps = {
      selectedFiles,
      onSelectFile: handleSelectFile,
      onSelectAll: handleSelectAll,
    };

    const mergeProps = {
      isMergeMode,
      mergeSelectedFiles,
      onSelectFileForMerge: handleSelectFileForMerge,
      onSelectAllForMerge: handleSelectAllForMerge,
    };

    switch (searchType) {
      case "timeline":
        return (
          <DocumentTimelineView
            {...commonProps}
            {...selectionProps}
            caseId={caseData?.id}
          />
        );

      case "chronological":
        return (
          <DocumentChronologicalView
            {...commonProps}
            {...selectionProps}
            {...mergeProps}
          />
        );

      default:
        return (
          <DocumentTypeView
            {...commonProps}
            {...mergeProps}
            onUnmergeFile={handleUndoMerge}
          />
        );
    }
  };

  const renderDocumentPreview = () => {
    if (!selectedDocument || !documentPreviewFile) return null;

    return (
      <DocumentPreview
        fileName={documentPreviewFile.name}
        fileUrl={fileUrl}
        fileType={fileType}
        file={selectedDocument}
        caseNumber={caseNumber}
        onClose={() => {
          setSelectedSnapshot(null);
          setShowSummaryPanel(false);
        }}
        onRetry={() => setPreviewAttempt((attempt) => attempt + 1)}
        isLoading={!fileUrl}
        fileLoadError={!fileUrl && !!selectedDocument}
      />
    );
  };

  const renderSummaryPanel = () => {
    if (!showSummaryPanel) return null;

    return (
      <section className="min-h-0 border-l border-border p-4">
        <SummaryPanel
          key={selectedDocument?.id}
          file={selectedDocument}
          isVisible={true}
          onClose={() => {
            setShowSummaryPanel(false);
          }}
        />
      </section>
    );
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    setMergeSelectedFiles([]);
  };

  return (
    <div className="grid h-full grid-cols-1 grid-rows-[auto_1fr] gap-2">
      {/* Search and Filters */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <div className="flex-1">
            <SemanticSearchInput />
          </div>

          <FileFilter
            onFiltersChange={handleFiltersChange}
            isStatusEditable={true}
          />
        </div>

        <div className="mt-2">
          <SearchTypeSelector value={searchType} onChange={handleSearchTypeChange} />
        </div>

        {canManageFiles && selectedFiles.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-primary px-4 py-3">
            <div>
              <p className="font-head text-xs font-semibold text-primary-foreground">
                {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""}{" "}
                selected
              </p>
              <p className="text-[11px] text-primary-foreground/70">
                Actions apply to the selected files
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="compact"
                className="text-primary-foreground/70 hover:bg-transparent hover:text-primary-foreground hover:underline"
                onClick={clearSelection}
                disabled={isDeleting}
              >
                Clear
              </Button>
              <DeleteMultipleFilesConfirmation
                fileIds={selectedFiles}
                onDeleteSuccess={clearSelection}
                buttonSize="compact"
                buttonVariant="destructive"
              />
              {mergeSelectedFiles.length >= 2 && (
                <Button
                  // Same solid blue as the Landing page's Continue/Login
                  // button (SignInFlow.tsx) — reads clearly as its own
                  // action against this bar's navy (bg-primary) background.
                  variant="secondary"
                  onClick={handleOpenMergeModal}
                  size="compact"
                  disabled={isDeleting}
                >
                  <Merge />
                  Merge
                  <span className="ml-0.5 rounded-full bg-primary-foreground/20 px-1.5 py-0.5 text-[10px] font-semibold leading-none">
                    {mergeSelectedFiles.length}
                  </span>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Files Views. min-w-0 alongside min-h-0: this Card is a grid item in
          the row above (grid-rows-[auto_1fr]), and grid items default to
          min-width: auto — they grow to fit content instead of respecting
          the track's width, defeating any descendant's horizontal
          scrolling. */}
      <Card
        className={cn(
          "min-h-0 min-w-0 overflow-hidden p-0",
          searchType !== null ? "overflow-hidden" : "overflow-auto"
        )}
      >
        {showSummaryPanel ? (
          // Fixed three-column layout when summary panel is open
          <div className="grid h-full grid-cols-3">
            <div
              className={cn(
                "min-h-0 min-w-0",
                searchType !== null
                  ? "overflow-auto overflow-x-auto"
                  : "overflow-visible"
              )}
            >
              {renderDocumentView()}
            </div>
            {renderDocumentPreview()}
            {renderSummaryPanel()}
          </div>
        ) : (
          // Resizable two-column layout when only document preview is open
          <ResizablePanels
            className="h-full"
            left={
              <div
                className={cn(
                  "h-full min-h-0 min-w-0",
                  searchType !== null
                    ? "overflow-auto overflow-x-auto"
                    : "overflow-visible"
                )}
              >
                {renderDocumentView()}
              </div>
            }
            right={renderDocumentPreview()}
            defaultRightWidthPercent={33}
            minRightWidthPercent={20}
            maxRightWidthPercent={65}
          />
        )}
      </Card>

      {/* Merge Documents Modal */}
      <MergeDocumentsModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        selectedFiles={files.filter((file) => mergeSelectedFiles.includes(file.id))}
        onMerge={handleMerge}
        isLoading={isMerging}
      />
    </div>
  );
};

export default DocumentSorterPage;
